/**
 * Argentina Province Analytics Integration
 * Enhanced tracking for Google Analytics and Amplitude with province-level data
 */

import { detectArgentinaProvinceFromTimezone, getArgentinaProvinceConfig, type ArgentinaProvinceConfig } from "@/config/argentinaProvinces";
// Amplitude tracking removed

// Google Analytics gtag function (global)
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Enhanced province detection with fallback methods
 */
export function detectArgentinaProvinceEnhanced(): {
  provinceCode: string | null;
  provinceName: string | null;
  region: string | null;
  economicLevel: string | null;
  detectionMethod: 'timezone' | 'test' | 'fallback' | 'none';
  confidence: 'high' | 'medium' | 'low';
} {
  if (typeof window === "undefined") {
    return {
      provinceCode: null,
      provinceName: null,
      region: null,
      economicLevel: null,
      detectionMethod: 'none',
      confidence: 'low'
    };
  }

  // Check for test province first (development)
  const testProvince = localStorage.getItem("test_argentina_province");
  if (testProvince && import.meta.env.DEV) {
    const config = getArgentinaProvinceConfig(testProvince);
    return {
      provinceCode: testProvince,
      provinceName: config?.provinceName || null,
      region: config?.region || null,
      economicLevel: config?.economicLevel || null,
      detectionMethod: 'test',
      confidence: 'high'
    };
  }

  // Try to detect from timezone (most reliable)
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const provinceFromTimezone = detectArgentinaProvinceFromTimezone(timezone);
  
  if (provinceFromTimezone) {
    const config = getArgentinaProvinceConfig(provinceFromTimezone);
    return {
      provinceCode: provinceFromTimezone,
      provinceName: config?.provinceName || null,
      region: config?.region || null,
      economicLevel: config?.economicLevel || null,
      detectionMethod: 'timezone',
      confidence: 'high'
    };
  }

  // Check if it's Argentina but we can't detect specific province
  if (timezone.includes("Argentina")) {
    return {
      provinceCode: "AR_UNKNOWN",
      provinceName: "Argentina (Provincia no detectada)",
      region: "UNKNOWN",
      economicLevel: "unknown",
      detectionMethod: 'fallback',
      confidence: 'medium'
    };
  }

  // No Argentina detected
  return {
    provinceCode: null,
    provinceName: null,
    region: null,
    economicLevel: null,
    detectionMethod: 'none',
    confidence: 'low'
  };
}

/**
 * Track province detection event in Amplitude
 */
export function trackProvinceDetection(): void {
  const detection = detectArgentinaProvinceEnhanced();
  
  if (detection.provinceCode) {
    // Amplitude tracking removed
  }
}

/**
 * Track template selection based on province
 */
export function trackProvinceTemplateSelection(templateName: string): void {
  const detection = detectArgentinaProvinceEnhanced();
  
  if (detection.provinceCode) {
    // Amplitude tracking removed

    // Also track in Google Analytics if available
    if (window.gtag) {
      window.gtag('event', 'argentina_template_selected', {
        custom_parameter_1: detection.provinceCode,
        custom_parameter_2: templateName,
        custom_parameter_3: detection.economicLevel,
        custom_parameter_4: detection.region
      });
    }
  }
}

/**
 * Track user interactions by province
 */
export function trackProvinceInteraction(interactionType: string, details: Record<string, unknown> = {}): void {
  const detection = detectArgentinaProvinceEnhanced();
  
  if (detection.provinceCode) {
    // Amplitude tracking removed
  }
}

/**
 * Track conversions by province (purchases, form submissions, etc.)
 */
export function trackProvinceConversion(conversionType: string, value?: number, currency?: string): void {
  const detection = detectArgentinaProvinceEnhanced();
  
  if (detection.provinceCode) {
    const eventData: Record<string, unknown> = {
      conversion_type: conversionType,
      province_code: detection.provinceCode,
      province_name: detection.provinceName,
      province_region: detection.region,
      economic_level: detection.economicLevel,
      timestamp: new Date().toISOString()
    };

    if (value !== undefined) {
      eventData.conversion_value = value;
    }
    if (currency) {
      eventData.currency = currency;
    }

    // Amplitude tracking removed

    // Enhanced Google Analytics conversion tracking
    if (window.gtag && value !== undefined) {
      window.gtag('event', 'purchase', {
        transaction_id: `ar_${detection.provinceCode}_${Date.now()}`,
        value: value,
        currency: currency || 'ARS',
        custom_parameter_1: detection.provinceCode,
        custom_parameter_2: detection.economicLevel,
        custom_parameter_3: detection.region
      });
    }
  }
}

/**
 * Track page views with province data
 */
export function trackProvincePageView(pagePath: string, pageTitle?: string): void {
  const detection = detectArgentinaProvinceEnhanced();
  
  if (detection.provinceCode) {
    // Amplitude tracking removed

    // Enhanced Google Analytics page view
    if (window.gtag) {
      window.gtag('config', 'G-QDGH3RF9KY', {
        page_title: pageTitle,
        page_location: window.location.href,
        custom_parameter_1: detection.provinceCode,
        custom_parameter_2: detection.economicLevel,
        custom_parameter_3: detection.region
      });
    }
  }
}

/**
 * Track template performance metrics by province
 */
export function trackProvinceTemplatePerformance(metrics: {
  templateName: string;
  loadTime?: number;
  interactionTime?: number;
  scrollDepth?: number;
  ctaClicks?: number;
}): void {
  const detection = detectArgentinaProvinceEnhanced();
  
  if (detection.provinceCode) {
    // Amplitude tracking removed
  }
}

/**
 * Get analytics data for province dashboard
 */
export function getProvinceAnalyticsData(): {
  currentProvince: ArgentinaProvinceConfig | null;
  detection: ReturnType<typeof detectArgentinaProvinceEnhanced>;
  sessionData: {
    sessionStart: string;
    pageViews: number;
    interactions: number;
  };
} {
  const detection = detectArgentinaProvinceEnhanced();
  const currentProvince = detection.provinceCode ? getArgentinaProvinceConfig(detection.provinceCode) : null;
  
  // Get session data from sessionStorage
  const sessionData = {
    sessionStart: sessionStorage.getItem('argentina_session_start') || new Date().toISOString(),
    pageViews: parseInt(sessionStorage.getItem('argentina_page_views') || '0'),
    interactions: parseInt(sessionStorage.getItem('argentina_interactions') || '0')
  };

  // Initialize session if needed
  if (!sessionStorage.getItem('argentina_session_start')) {
    sessionStorage.setItem('argentina_session_start', sessionData.sessionStart);
  }

  return {
    currentProvince,
    detection,
    sessionData
  };
}

/**
 * Initialize province analytics tracking
 */
export function initProvinceAnalytics(): void {
  // Track initial province detection
  trackProvinceDetection();
  
  // Set up session tracking
  const sessionData = getProvinceAnalyticsData().sessionData;
  sessionStorage.setItem('argentina_session_start', sessionData.sessionStart);
  
  // Track initial page view
  trackProvincePageView(window.location.pathname, document.title);
  
  // Increment page view counter
  const currentPageViews = parseInt(sessionStorage.getItem('argentina_page_views') || '0');
  sessionStorage.setItem('argentina_page_views', (currentPageViews + 1).toString());
  
  if (import.meta.env.DEV) {
    // intentional: dev-only no-op branch reserved for future logging
  }
}

/**
 * Batch tracking for multiple events (performance optimization)
 */
export function batchTrackProvinceEvents(events: Array<{
  type: 'interaction' | 'conversion' | 'performance';
  data: Record<string, unknown>;
}>): void {
  const detection = detectArgentinaProvinceEnhanced();
  
  if (detection.provinceCode && events.length > 0) {
    const batchData = {
      province_code: detection.provinceCode,
      province_name: detection.provinceName,
      province_region: detection.region,
      economic_level: detection.economicLevel,
      batch_size: events.length,
      events: events,
      timestamp: new Date().toISOString()
    };

    // Amplitude tracking removed
  }
}

// Export all functions for easy use
export default {
  detectArgentinaProvinceEnhanced,
  trackProvinceDetection,
  trackProvinceTemplateSelection,
  trackProvinceInteraction,
  trackProvinceConversion,
  trackProvincePageView,
  trackProvinceTemplatePerformance,
  getProvinceAnalyticsData,
  initProvinceAnalytics,
  batchTrackProvinceEvents
};